import { z } from 'zod';

export const UserRole = z.enum([
  'admin',
  'agent',
  'assistant',
  'buyer',
  'seller',
  'lender',
  'escrow_officer',
  'appraiser',
  'vendor',
]);
export type UserRole = z.infer<typeof UserRole>;

export const AgentLikeRole = z.enum(['admin', 'agent', 'assistant']);
export type AgentLikeRole = z.infer<typeof AgentLikeRole>;

export const AGENT_LIKE_ROLES: AgentLikeRole[] = ['admin', 'agent', 'assistant'];

export function isAgentLikeRole(role: UserRole): role is AgentLikeRole {
  return AGENT_LIKE_ROLES.includes(role as AgentLikeRole);
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  agent: 'Agent',
  assistant: 'Assistant',
  buyer: 'Buyer',
  seller: 'Seller',
  lender: 'Lender',
  escrow_officer: 'Escrow Officer',
  appraiser: 'Appraiser',
  vendor: 'Vendor',
};

export function formatUserRole(role: UserRole): string {
  return USER_ROLE_LABELS[role];
}

export const User = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  name: z.string().min(1),
  role: UserRole,
  /** Fresh presigned URL when the user has uploaded a profile photo,
   *  null otherwise. UI falls back to a deterministic initials avatar. */
  photoUrl: z.string().nullable().optional(),
  /**
   * Verified-contact state (0245). Ask YRP AI is the only endpoint an outside
   * party can make us spend money on, so it requires a confirmed email AND a
   * confirmed phone — see requireVerifiedContact in apps/api/plugins/auth.
   *
   * All three are `.optional()` rather than required, deliberately: web-public
   * prerenders against the LIVE (older) API at build time and parses responses
   * through these schemas, so a newly-required field on a shared response fails
   * that build while the API deploy succeeds — the site then silently stays on
   * its previous build. Optional until both apps have shipped.
   */
  phone: z.string().nullable().optional(),
  /** Set when a magic link is redeemed. Null/absent = unverified. */
  emailVerifiedAt: z.coerce.date().nullable().optional(),
  /** Set when an SMS code is confirmed. Null/absent = unverified. */
  phoneVerifiedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
});
export type User = z.infer<typeof User>;
